import {
  ShoppingBag, Users2, Wallet, Settings2, Cog, HelpCircle,
} from 'lucide-react'

// =============================================================================
// SINGLE SOURCE OF TRUTH for the reorganised (workflow-grouped) navigation tree.
// Consumed by the live sidebar (AccordionNav) and the /nav-preview demo. This is
// RE-HOMING ONLY: every leaf points at its exact existing route and carries its
// existing permission key (pkey). Nothing here creates a route or a key.
//
// Ordering is by WORKFLOW, never alphabetical. Retail vertical only — Logistics
// and School keep their own separate vertical navs and are intentionally absent.
// =============================================================================

export type Leaf = {
  label: string
  to: string
  // Existing permission key, unchanged by the move. Undefined = action-gated /
  // ungated (the existing Sales Report, Help).
  pkey?: string
  note?: string
  // Only the top-level leaves (Dashboard, Help) carry an icon for the collapsed rail.
  icon?: React.ReactNode
}
export type NavNode = {
  label: string
  // Parent-is-link (Finance pattern): the label navigates here AND expands; the
  // chevron toggles without navigating. Omit for a pure grouping row.
  to?: string
  pkey?: string
  icon?: React.ReactNode
  // Only the top sections carry an icon and honour strict one-open-at-a-time.
  children: Array<NavNode | Leaf>
}

export function isNode(n: NavNode | Leaf): n is NavNode {
  return (n as NavNode).children !== undefined
}
export const pathOf = (to: string) => to.split('?')[0]

export const NAV_TREE: Array<NavNode | Leaf> = [
  { label: 'Dashboard', to: '/dashboard', pkey: 'dashboard' },

  {
    label: 'Trading', to: '/trading', icon: <ShoppingBag className="h-[18px] w-[18px]" />,
    children: [
      {
        label: 'Purchasing',
        children: [
          { label: 'Suppliers', to: '/contacts?tab=suppliers', pkey: 'contacts.list' },
          { label: 'Buy', to: '/buy/purchases', pkey: 'buy.purchases' },
          { label: 'Goods in Transit', to: '/buy/in-transit', pkey: 'buy.in_transit' },
          { label: 'Purchase Returns', to: '/buy/returns', pkey: 'buy.returns' },
          {
            label: 'Purchase Reports',
            children: [
              { label: 'Product Purchases', to: '/reports/product-purchases', pkey: 'trading.product_purchases' },
              { label: 'Supplier Ageing', to: '/reports/aged-creditors', pkey: 'reporting.aged_creditors' },
              { label: 'Purchase Payments', to: '/reports/purchase-payments', pkey: 'trading.purchase_payments' },
              { label: 'Vendor Payments', to: '/payments/vendors', pkey: 'trading.payments.vendors' },
            ],
          },
        ],
      },
      {
        label: 'Inventory',
        children: [
          { label: 'Products', to: '/inventory', pkey: 'inventory.products' },
          { label: 'Low Stock', to: '/inventory/low-stock', pkey: 'inventory.low_stock' },
          { label: 'Stock Take', to: '/inventory/stock-take', pkey: 'inventory.stock_take' },
          { label: 'Stock Transfers', to: '/inventory/transfers', pkey: 'inventory.transfers' },
          { label: 'Stock Entries', to: '/stock', pkey: 'inventory.stock' },
          { label: 'Locations', to: '/locations', pkey: 'inventory.locations' },
          { label: 'Barcode Scanner', to: '/barcode', pkey: 'inventory.barcode' },
          { label: 'Inventory Valuation', to: '/reports/inventory-valuation', pkey: 'reporting.inventory_valuation' },
          { label: 'Product Movement', to: '/reports/product-movement', pkey: 'inventory.product_movement' },
        ],
      },
      {
        label: 'Sell',
        children: [
          { label: 'Customers', to: '/contacts?tab=customers', pkey: 'contacts.list' },
          { label: 'Credit Limits', to: '/customers/credit-limits', pkey: 'contacts.credit_limits' },
          { label: 'Invoices', to: '/sell/invoices', pkey: 'sell.invoices' },
          { label: 'POS', to: '/sell/pos', pkey: 'sell.pos' },
          { label: 'POS Sessions', to: '/sell/pos-sessions', pkey: 'sell.pos_sessions' },
          { label: 'Online Orders', to: '/sell/online-orders', pkey: 'sell.online_orders' },
          { label: 'Sales Returns', to: '/sell/returns', pkey: 'sell.returns' },
          {
            label: 'Sales Reports',
            children: [
              { label: 'Sales Report', to: '/sell/report' },
              { label: 'Product Sales', to: '/reports/product-sales', pkey: 'reporting.sales_analysis' },
              { label: 'Customer / Receivables Ageing', to: '/reports/aged-debtors', pkey: 'reporting.aged_debtors' },
              { label: 'Sales per Rep', to: '/sales-per-rep', pkey: 'trading.sales_per_rep' },
              { label: 'Incentives', to: '/incentives', pkey: 'trading.incentives' },
              { label: 'Incentives Payable', to: '/incentive-payables', pkey: 'trading.incentive_payables' },
              { label: 'Sales Payments', to: '/reports/sales-payments', pkey: 'trading.sales_payments' },
              { label: 'Customer Payments', to: '/payments/customers', pkey: 'trading.payments.customers' },
            ],
          },
        ],
      },
      {
        label: 'Expenses',
        children: [
          { label: 'Expenses', to: '/expenses', pkey: 'expenses.list' },
          { label: 'Expense Payments', to: '/payments/expenses', pkey: 'trading.payments.expenses' },
        ],
      },
    ],
  },

  {
    label: 'Payroll', to: '/payroll', icon: <Users2 className="h-[18px] w-[18px]" />,
    children: [
      { label: 'Staff', to: '/payroll/staff', pkey: 'payroll.staff' },
      { label: 'Run Payroll', to: '/payroll/run', pkey: 'payroll.run' },
      { label: 'Payslips', to: '/payroll/payslips', pkey: 'payroll.payslips' },
      { label: 'Salary Payments', to: '/payroll/salary-payments', pkey: 'payroll.payslips' },
      {
        label: 'Setup',
        children: [
          { label: 'Bulk Setup', to: '/payroll/settings/bulk-setup', pkey: 'payroll.settings_bulk' },
          { label: 'Tax & Statutory', to: '/payroll/settings/tax', pkey: 'payroll.settings_tax' },
          { label: 'Job Grades & Structure', to: '/payroll/settings/grades', pkey: 'payroll.settings_grades' },
          { label: 'Salary by Grade', to: '/payroll/settings/salary', pkey: 'payroll.settings_salary' },
          { label: 'Working Schedules', to: '/payroll/settings/schedules', pkey: 'payroll.settings_schedules' },
          { label: 'Job Titles', to: '/payroll/settings/jobs', pkey: 'payroll.settings_jobs' },
        ],
      },
    ],
  },

  {
    label: 'Finance', to: '/finance', icon: <Wallet className="h-[18px] w-[18px]" />,
    children: [
      { label: 'Accounting Hub', to: '/finance/accounting', pkey: 'accounting.hub' },
      { label: 'Reporting Hub', to: '/finance/reporting', pkey: 'reporting.hub' },
      { label: 'Fixed Assets', to: '/finance/fixed-assets', pkey: 'finance.fixed_assets.register' },
      { label: 'Tax Compliance', to: '/finance/tax-compliance', pkey: 'finance.tax_compliance.cit' },
    ],
  },

  {
    label: 'Business Settings', to: '/business-settings', icon: <Settings2 className="h-[18px] w-[18px]" />,
    children: [
      { label: 'Company Profile', to: '/business-settings/profile', pkey: 'business_settings.profile' },
      { label: 'Storefront · Payments · Delivery', to: '/business-settings/commerce', pkey: 'business_settings.commerce' },
      { label: 'Invoice Numbering', to: '/business-settings/invoice-numbering', pkey: 'business_settings.invoice_numbering' },
      { label: 'Printer Settings', to: '/business-settings/printer-settings', pkey: 'business_settings.printer' },
      { label: 'Locations', to: '/business-settings/locations', pkey: 'business_settings.locations' },
      { label: 'Finance Settings', to: '/business-settings/finance', pkey: 'business_settings.finance' },
      { label: 'Module Permissions', to: '/settings/role-permissions', pkey: 'business_settings.role_permissions_admin' },
      { label: 'Tenant Modules', to: '/settings/modules', pkey: 'business_settings.tenant_modules' },
      { label: 'Custom Fields', to: '/settings/custom-fields', pkey: 'business_settings.custom_fields' },
    ],
  },

  {
    label: 'System', to: '/system', icon: <Cog className="h-[18px] w-[18px]" />,
    children: [
      { label: 'Users', to: '/business-settings/users', pkey: 'business_settings.users' },
      { label: 'Roles & Permissions', to: '/business-settings/roles', pkey: 'business_settings.roles' },
      { label: 'Audit Trail', to: '/system/audit', pkey: 'system.audit' },
      { label: 'Notifications', to: '/system/notifications', pkey: 'system.notifications' },
      { label: 'Palstro Messages', to: '/system/messages', pkey: 'system.messages' },
      { label: 'Support', to: '/support', pkey: 'system.support' },
      { label: 'Subscription', to: '/subscription', pkey: 'system.subscription' },
      { label: 'My Profile', to: '/settings/profile', pkey: 'system.profile' },
    ],
  },

  { label: 'Help', to: '/help', icon: <HelpCircle className="h-[18px] w-[18px]" /> },
]

// Every group id (label path), for expand-all.
export function collectGroupIds(nodes: Array<NavNode | Leaf>, prefix = ''): string[] {
  const out: string[] = []
  for (const n of nodes) {
    if (isNode(n)) {
      const id = prefix + n.label
      out.push(id)
      out.push(...collectGroupIds(n.children, id + '/'))
    }
  }
  return out
}

// Σ attention counts across all descendant leaves of a node.
export function descendantCount(node: NavNode | Leaf, countFor: (to: string) => number): number {
  if (!isNode(node)) return node.to ? countFor(node.to) : 0
  return node.children.reduce((s, c) => s + descendantCount(c, countFor), 0)
}

// A leaf is visible if the user can see its page (keyless leaves are always shown);
// a group is visible if any descendant is.
export function anyVisible(node: NavNode | Leaf, canSee: (k: string) => boolean): boolean {
  if (!isNode(node)) return node.pkey ? canSee(node.pkey) : true
  return node.children.some(c => anyVisible(c, canSee))
}
